import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  DeleteVideoCommentResult,
  DeleteVideoCommentOptions,
  PatchVideoCommentResult,
  PatchVideoCommentOptions,
  GetVideoCommentOptions,
  GetVideoCommentResult,
  getVideoComment,
  DeleteVideoCommentFields,
  PatchVideoCommentFields,
  GetVideoCommentFields,
  deleteVideoComment,
  patchVideoComment,
} from '../fetchers/video/comment';
import { useCallback } from 'react';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetVideoCommentOptions<Fields extends GetVideoCommentFields> =
  GetVideoCommentOptions<Fields>;

/**
 * Get a specific video comment
 * This method returns the specified video comment.
 */

export function useGetVideoComment<Fields extends GetVideoCommentFields>(
  getOptions: UseGetVideoCommentOptions<Fields> | (() => UseGetVideoCommentOptions<Fields> | null),
  config?: SWRConfiguration<GetVideoCommentResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options
      ? `/videos/${options.where.videoId}/comments/${options.where.commentId}${serializeQuery(
          options,
        )}`
      : () => null,
    options
      ? () => {
          return getVideoComment({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoComment, {
    endpoint: '/videos/:videoId/comments/:commentId',
    method: 'GET',
  });
}

/**
 * Get a specific video comment
 * This method returns the specified video comment.
 */

export function useGetVideoCommentLazy<Fields extends GetVideoCommentFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<GetVideoCommentResult<Fields> | undefined>();

  let callback = useCallback(
    async (options: UseGetVideoCommentOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/videos/${options.where.videoId}/comments/${options.where.commentId}${serializeQuery(
            options,
          )}`,
          getVideoComment({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoCommentLazy, {
    endpoint: '/videos/:videoId/comments/:commentId',
    method: 'GET',
  });
}

type UseDeleteVideoCommentOptions<Fields extends DeleteVideoCommentFields> =
  DeleteVideoCommentOptions<Fields>;

/**
 * Delete a video comment
 * This method deletes the specified video comment. The authenticated user must be the owner of the comment.
 */

export function useDeleteVideoComment<Fields extends DeleteVideoCommentFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<DeleteVideoCommentResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UseDeleteVideoCommentOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/videos/${options.where.videoId}/comments/${options.where.commentId}${serializeQuery(
            options,
          )}`,
          deleteVideoComment({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useDeleteVideoComment, {
    endpoint: '/videos/:videoId/comments/:commentId',
    method: 'DELETE',
  });
}

type UsePatchVideoCommentOptions<Fields extends PatchVideoCommentFields> =
  PatchVideoCommentOptions<Fields>;

/**
 * Edit a video comment
 * This method edits the specified video comment. The authenticated user must be the owner of the comment.
 */

export function usePatchVideoComment<Fields extends PatchVideoCommentFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<PatchVideoCommentResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UsePatchVideoCommentOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/videos/${options.where.videoId}/comments/${options.where.commentId}${serializeQuery(
            options,
          )}`,
          patchVideoComment({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePatchVideoComment, {
    endpoint: '/videos/:videoId/comments/:commentId',
    method: 'PATCH',
  });
}
